import { Fragment, memo, useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { ehRotaInterna } from '@malgoos/shared';
import { useTranslation } from 'react-i18next';
import { Archive, ArchiveRestore, ArrowLeft, CornerUpLeft, ExternalLink, LogOut, MessagesSquare, MoreHorizontal, Pencil, Trash2, UsersRound } from 'lucide-react';
import { Botao, Selo } from '../../componentes/base';
import { Carregando, Erro, Vazio } from '../../componentes/estados';
import { Menu, type ItemMenu } from '../../componentes/menu';
import { Confirmar } from '../../componentes/sobreposicao';
import { TextoCortado } from '../../componentes/texto-cortado';
import { useToast } from '../../componentes/toast';
import { ErroRequisicao, del, get, mensagemDeErro, patch, post } from '../../nucleo/api';
import { dataHora, hora, nomePessoa, textoLivre } from '../../nucleo/formato';
import { rotuloEntidade } from '../../nucleo/rotulos';
import { useSessao } from '../../nucleo/sessao';
import { Composer } from './Composer';
import { AnexosMensagem, TextoMensagem } from './Conteudo';
import { useIntervaloEquipe, useNomesDoChat } from './dados';
import { AvatarCanal } from './ListaCanais';
import { EditarCanal, Membros } from './Modais';
import { textoDoSistema } from './textos';
import { nomeDoCanalNaTela, type CanalDetalhe, type Mensagem, type Pagina } from './tipos';
import { carregarMais } from '../../nucleo/foco';

/** A mensagem é de outra pessoa, de texto, e chegou depois da minha última leitura (divisória de novas). */
export const ehNovaParaMim = (m: Pick<Mensagem, 'tipo' | 'autor_id' | 'created_at' | 'apagada'>, lidoAte: string | null, eu: string) =>
  !!lidoAte && m.tipo === 'texto' && !m.apagada && m.autor_id !== eu && new Date(m.created_at).getTime() > new Date(lidoAte).getTime();

/** Gatilho só de ícone (o rótulo vai no aria-label): ações da mensagem e do canal. */
export const GATILHO_MENSAGEM = 'mt-1 rounded-md p-1.5 text-tinta-3 hover:bg-sup-3 hover:text-tinta-1';
const GATILHO_CABECALHO = 'inline-flex size-8 items-center justify-center rounded-lg border border-borda bg-sup-2 text-tinta-1 hover:bg-sup-3';

interface PropsBolha {
  m: Mensagem;
  eu: string;
  mostrarAutor: boolean;
  podeEscrever: boolean;
  editando: boolean;
  aoResponder: (m: Mensagem) => void;
  aoEditar: (m: Mensagem | null) => void;
  aoSalvarEdicao: (m: Mensagem, texto: string) => void;
  aoApagar: (m: Mensagem) => void;
  salvando: boolean;
  /** W20: nomes das pessoas do chat (iniciais no texto com o modo discreto e a opção de nomes). */
  nomes: readonly string[];
}

function EdicaoInline({ m, aoSalvar, aoCancelar, salvando }: { m: Mensagem; aoSalvar: (texto: string) => void; aoCancelar: () => void; salvando: boolean }) {
  const { t } = useTranslation('equipe');
  const [texto, setTexto] = useState(m.texto);
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <textarea
        aria-label={t('mensagem.editarRotulo')}
        rows={3}
        autoFocus
        value={texto}
        maxLength={10000}
        onChange={(e) => setTexto(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            aoCancelar();
          }
          if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            aoSalvar(texto);
          }
        }}
        className="w-full min-w-0 resize-y rounded-lg border border-borda-campo bg-campo px-3 py-2 text-sm text-tinta-1 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco"
      />
      <div className="flex justify-end gap-2">
        <Botao variante="fantasma" tamanho="sm" onClick={aoCancelar}>
          {t('mensagem.cancelar')}
        </Botao>
        {/* Review r17 visual M2: secundário; o primário da conversa aberta é o "Enviar" do compositor, que segue à vista. */}
        <Botao tamanho="sm" carregando={salvando} disabled={!texto.trim() && !m.anexos.length} onClick={() => aoSalvar(texto)}>
          {t('mensagem.salvar')}
        </Botao>
      </div>
    </div>
  );
}

/** Bolha memorizada: o sinal recarrega a página e a bolha que não mudou não refaz a censura do texto. */
const Bolha = memo(function Bolha({ m, eu, mostrarAutor, podeEscrever, editando, aoResponder, aoEditar, aoSalvarEdicao, aoApagar, salvando, nomes }: PropsBolha) {
  const { t } = useTranslation('equipe');
  if (m.tipo === 'sistema') {
    return (
      <li className="flex justify-center" data-mensagem={m.id}>
        <span className="rounded-full border border-borda px-3 py-1 text-center text-xs text-tinta-3">
          {textoDoSistema(t, m.parametros, m.texto, nomes)} <span className="numero">{hora(m.created_at)}</span>
        </span>
      </li>
    );
  }
  const minha = m.autor_id === eu;
  const acoes: ItemMenu[] = [];
  if (!m.apagada && podeEscrever) acoes.push({ chave: 'responder', rotulo: t('mensagem.responder'), icone: <CornerUpLeft className="size-4" aria-hidden />, aoClicar: () => aoResponder(m) });
  if (!m.apagada && minha && podeEscrever) acoes.push({ chave: 'editar', rotulo: t('mensagem.editar'), icone: <Pencil className="size-4" aria-hidden />, aoClicar: () => aoEditar(m) });
  if (!m.apagada && minha) acoes.push({ chave: 'apagar', rotulo: t('mensagem.apagar'), icone: <Trash2 className="size-4" aria-hidden />, aoClicar: () => aoApagar(m) });
  return (
    <li className={`flex items-start gap-1 ${minha ? 'justify-end' : 'justify-start'}`} data-mensagem={m.id}>
      {minha && acoes.length > 0 && <Menu rotulo={t('mensagem.acoes')} gatilho={<MoreHorizontal className="size-4" aria-hidden />} classeGatilho={GATILHO_MENSAGEM} itens={acoes} classePainel="right-0 w-48" />}
      <div className={`flex min-w-0 flex-col gap-1.5 rounded-2xl border px-3.5 py-2 ${editando ? 'w-full max-w-md' : 'max-w-full sm:max-w-md'} ${minha ? 'border-acento-forte bg-acento-suave' : 'border-borda bg-sup-2'}`}>
        {mostrarAutor && !minha && <span className="text-xs font-semibold text-tinta-2">{nomePessoa(m.autor_nome)}</span>}
        {m.resposta && (
          <div className="min-w-0 border-l-2 border-acento-forte pl-2 text-xs">
            <p className="font-medium text-tinta-2">{nomePessoa(m.resposta.autor_nome)}</p>
            <TextoCortado como="p" className="text-tinta-3">
              {m.resposta.apagada ? t('mensagem.apagada') : m.resposta.texto ? textoLivre(m.resposta.texto, ...nomes) : t('mensagem.anexo')}
            </TextoCortado>
          </div>
        )}
        {m.apagada ? (
          <p className="text-sm text-tinta-3 italic">{t('mensagem.apagada')}</p>
        ) : editando ? (
          <EdicaoInline m={m} salvando={salvando} aoCancelar={() => aoEditar(null)} aoSalvar={(texto) => aoSalvarEdicao(m, texto)} />
        ) : (
          m.texto && <TextoMensagem texto={m.texto} mencionados={m.mencoes.map((x) => x.nome)} nomes={nomes} />
        )}
        {!m.apagada && <AnexosMensagem canalId={m.canal_id} anexos={m.anexos} />}
        <div className="flex flex-wrap items-center justify-end gap-x-2 gap-y-0.5 text-xs text-tinta-3">
          {m.origem === 'agente' && <span>{t('mensagem.viaAgente')}</span>}
          {m.editada_em && !m.apagada && <span title={dataHora(m.editada_em)}>{t('mensagem.editada')}</span>}
          <span className="numero" title={dataHora(m.created_at)}>
            {hora(m.created_at)}
          </span>
        </div>
      </div>
      {!minha && acoes.length > 0 && <Menu rotulo={t('mensagem.acoes')} gatilho={<MoreHorizontal className="size-4" aria-hidden />} classeGatilho={GATILHO_MENSAGEM} itens={acoes} classePainel="left-0 w-48" />}
    </li>
  );
});

/** Conversa à direita: cabeçalho com ações, mensagens (divisória de novas) e campo de envio. */
export function PainelCanal({ canalId, aoVoltar, aoSaiu }: { canalId: string; aoVoltar: () => void; aoSaiu: () => void }) {
  const { t } = useTranslation('equipe');
  const s = useSessao();
  const eu = s.usuario.id;
  const toast = useToast();
  const qc = useQueryClient();
  const intervalo = useIntervaloEquipe(5_000);
  const fim = useRef<HTMLDivElement>(null);
  const [resposta, setResposta] = useState<Mensagem | null>(null);
  const [editando, setEditando] = useState<string | null>(null);
  const [apagando, setApagando] = useState<Mensagem | null>(null);
  const [membros, setMembros] = useState(false);
  const [editarCanal, setEditarCanal] = useState(false);
  const [confirmar, setConfirmar] = useState<'sair' | 'arquivar' | null>(null);
  // Até onde eu tinha lido ao abrir: a divisória de novas fica no lugar enquanto o canal está aberto.
  const [lidoAoAbrir, setLidoAoAbrir] = useState<{ canal: string; lido: string | null } | null>(null);

  const q = useQuery({ queryKey: ['equipe', 'canal', canalId], queryFn: () => get<CanalDetalhe>(`/equipe/canais/${canalId}`), refetchInterval: intervalo, retry: (n, e) => !(e instanceof ErroRequisicao && e.status === 404) && n < 2 });
  const canal = q.data;
  useEffect(() => {
    if (canal && lidoAoAbrir?.canal !== canal.id) setLidoAoAbrir({ canal: canal.id, lido: canal.lido_ate });
  }, [canal, lidoAoAbrir]);

  const msgs = useInfiniteQuery({
    queryKey: ['equipe', 'mensagens', canalId],
    queryFn: ({ pageParam }) => get<Pagina<Mensagem>>(`/equipe/canais/${canalId}/mensagens`, { cursor: pageParam || undefined, limite: 50 }),
    initialPageParam: '',
    getNextPageParam: (p) => p.proximo_cursor ?? undefined,
    refetchInterval: intervalo,
    enabled: !!canal,
  });
  // As páginas vêm da mais nova para a mais antiga; a tela mostra da mais antiga para a mais nova.
  const mensagens = useMemo(() => (msgs.data ? [...msgs.data.pages].reverse().flatMap((p) => [...p.itens].reverse()) : []), [msgs.data]);
  const ultimoId = mensagens[mensagens.length - 1]?.id;
  // W20: nomes que o texto pode citar: as pessoas do chat, os participantes, os autores e os mencionados.
  const nomes = useNomesDoChat([
    ...(canal?.participantes ?? []).map((p) => p.nome),
    canal?.outro?.nome,
    ...mensagens.flatMap((m) => [m.autor_nome, m.resposta?.autor_nome, ...m.mencoes.map((x) => x.nome), m.parametros?.ator, ...(m.parametros?.alvos ?? [])]),
  ]);
  useEffect(() => {
    fim.current?.scrollIntoView({ block: 'end' });
  }, [ultimoId, canalId]);

  // Marca como lido ao abrir, quando chega mensagem com o canal aberto e quando a aba volta a ficar visível.
  const lida = useMutation({
    mutationFn: () => post(`/equipe/canais/${canalId}/lido`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['equipe', 'nao-lidas'] });
      void qc.invalidateQueries({ queryKey: ['equipe', 'canais'] });
    },
  });
  const naoLidas = canal?.nao_lidas ?? 0;
  const participa = !!canal?.participa;
  const marcar = lida.mutate;
  useEffect(() => {
    const talvez = () => {
      if (participa && naoLidas > 0 && document.visibilityState === 'visible') marcar();
    };
    talvez();
    document.addEventListener('visibilitychange', talvez);
    return () => document.removeEventListener('visibilitychange', talvez);
  }, [canalId, naoLidas, participa, marcar]);

  const invalidar = () => qc.invalidateQueries({ queryKey: ['equipe'] });
  const salvarEdicao = useMutation({
    mutationFn: ({ m, texto }: { m: Mensagem; texto: string }) => {
      const nomes = canal?.participantes ?? [];
      const mencoes = nomes.filter((p) => p.usuario_id !== eu && texto.includes(`@${p.nome}`)).map((p) => p.usuario_id);
      return patch(`/equipe/mensagens/${m.id}`, { texto, mencoes });
    },
    onSuccess: async () => {
      setEditando(null);
      await invalidar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const apagar = useMutation({
    mutationFn: (m: Mensagem) => del(`/equipe/mensagens/${m.id}`),
    onSuccess: async () => {
      setApagando(null);
      await invalidar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const entrar = useMutation({ mutationFn: () => post(`/equipe/canais/${canalId}/entrar`), onSuccess: invalidar, onError: (e) => toast.erro(mensagemDeErro(e)) });
  const sair = useMutation({
    mutationFn: () => post(`/equipe/canais/${canalId}/sair`),
    onSuccess: async () => {
      setConfirmar(null);
      await invalidar();
      aoSaiu();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const arquivar = useMutation({
    mutationFn: (arquivado: boolean) => post(`/equipe/canais/${canalId}/arquivar`, { arquivado }),
    onSuccess: async () => {
      setConfirmar(null);
      await invalidar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });

  if (q.isPending) return <Carregando />;
  if (q.isError) {
    if (q.error instanceof ErroRequisicao && q.error.status === 404) return <Vazio icone={<MessagesSquare className="size-5" aria-hidden />} titulo={t('painel.escolhaTitulo')} texto={t('painel.naoEncontrado')} />;
    return <Erro erro={q.error} aoTentar={() => q.refetch()} />;
  }
  if (!canal) return null;
  const nome = nomeDoCanalNaTela(canal, nomes);
  const lidoAte = lidoAoAbrir?.canal === canal.id ? lidoAoAbrir.lido : canal.lido_ate;
  const indiceNovas = mensagens.findIndex((m) => ehNovaParaMim(m, lidoAte, eu));
  const acoesCanal: ItemMenu[] = [];
  if (canal.pode_gerir) acoesCanal.push({ chave: 'editar', rotulo: t('acoes.editar'), icone: <Pencil className="size-4" aria-hidden />, aoClicar: () => setEditarCanal(true) });
  if (canal.pode_gerir) acoesCanal.push(canal.arquivado ? { chave: 'desarquivar', rotulo: t('acoes.desarquivar'), icone: <ArchiveRestore className="size-4" aria-hidden />, aoClicar: () => arquivar.mutate(false) } : { chave: 'arquivar', rotulo: t('acoes.arquivar'), icone: <Archive className="size-4" aria-hidden />, aoClicar: () => setConfirmar('arquivar') });
  if (canal.participa && canal.tipo !== 'direta') acoesCanal.push({ chave: 'sair', rotulo: t('acoes.sair'), icone: <LogOut className="size-4" aria-hidden />, aoClicar: () => setConfirmar('sair') });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-borda px-3 py-3">
        <Botao variante="fantasma" tamanho="sm" className="md:hidden" aria-label={t('acoes.voltar')} icone={<ArrowLeft className="size-4" />} onClick={aoVoltar} />
        <AvatarCanal canal={canal} />
        <div className="min-w-0 flex-1 basis-40">
          <p className="flex min-w-0 items-center gap-2">
            <TextoCortado como="span" className="font-semibold" data-titulo-canal>
              {nome}
            </TextoCortado>
            {canal.tipo !== 'direta' && <Selo cor={canal.visibilidade === 'publico' ? 'verde' : 'neutro'}>{t(`visibilidade.${canal.visibilidade}`)}</Selo>}
            {canal.arquivado && <Selo cor="laranja">{t('lista.arquivadas')}</Selo>}
          </p>
          <TextoCortado como="p" className="text-xs text-tinta-3">
            {canal.tipo === 'registro' && canal.vinculo_entidade ? t('painel.discussaoDe', { entidade: rotuloEntidade(canal.vinculo_entidade) }) : t(`tipos.${canal.tipo}`)}
            {canal.descricao ? ` · ${textoLivre(canal.descricao, ...nomes)}` : ''}
          </TextoCortado>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {canal.vinculo_link && ehRotaInterna(canal.vinculo_link) && (
            <Link to={canal.vinculo_link} className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-borda bg-sup-2 px-3 text-sm font-medium text-tinta-1 hover:bg-sup-3">
              <ExternalLink className="size-4" aria-hidden />
              {t('acoes.abrirRegistro')}
            </Link>
          )}
          {canal.tipo !== 'direta' && (
            <Botao tamanho="sm" icone={<UsersRound className="size-4" />} onClick={() => setMembros(true)} aria-label={`${t('acoes.participantes')}: ${t('lista.membros', { count: canal.membros })}`}>
              <span className="numero">{canal.membros}</span>
            </Botao>
          )}
          {acoesCanal.length > 0 && <Menu rotulo={t('acoes.acoesCanal')} gatilho={<MoreHorizontal className="size-4" aria-hidden />} classeGatilho={GATILHO_CABECALHO} itens={acoesCanal} classePainel="right-0 w-56" />}
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-4" aria-live="polite" aria-relevant="additions">
        {msgs.isPending ? (
          <Carregando />
        ) : msgs.isError ? (
          <Erro erro={msgs.error} aoTentar={() => msgs.refetch()} />
        ) : !mensagens.some((m) => m.tipo === 'texto') && !msgs.hasNextPage ? (
          <>
            <ul className="mb-4 flex flex-col gap-2">
              {mensagens.map((m) => (
                <Bolha key={m.id} m={m} eu={eu} mostrarAutor={false} podeEscrever={false} editando={false} aoResponder={setResposta} aoEditar={() => undefined} aoSalvarEdicao={() => undefined} aoApagar={setApagando} salvando={false} nomes={nomes} />
              ))}
            </ul>
            <Vazio icone={<MessagesSquare className="size-5" aria-hidden />} titulo={t('painel.semMensagensTitulo')} texto={t('painel.semMensagens')} />
          </>
        ) : (
          <>
            {msgs.hasNextPage && (
              <div className="mb-3 flex justify-center">
                <Botao variante="fantasma" tamanho="sm" carregando={msgs.isFetchingNextPage} onClick={(e) => carregarMais(() => msgs.fetchNextPage(), e.currentTarget, { anteriores: true })}>
                  {t('painel.carregarAnteriores')}
                </Botao>
              </div>
            )}
            <ul className="flex flex-col gap-2">
              {mensagens.map((m, i) => (
                <Fragment key={m.id}>
                  {i === indiceNovas && (
                    <li data-divisoria-novas className="flex items-center gap-2 text-xs font-medium text-acento-forte">
                      <span className="h-px flex-1 bg-acento-forte" aria-hidden />
                      {t('painel.novas')}
                      <span className="h-px flex-1 bg-acento-forte" aria-hidden />
                    </li>
                  )}
                  <Bolha
                    m={m}
                    eu={eu}
                    mostrarAutor={canal.tipo !== 'direta' && (i === 0 || mensagens[i - 1]!.autor_id !== m.autor_id || mensagens[i - 1]!.tipo !== 'texto')}
                    podeEscrever={canal.pode_escrever}
                    editando={editando === m.id}
                    aoResponder={setResposta}
                    aoEditar={(x) => setEditando(x?.id ?? null)}
                    aoSalvarEdicao={(x, texto) => salvarEdicao.mutate({ m: x, texto })}
                    aoApagar={setApagando}
                    salvando={salvarEdicao.isPending}
                    nomes={nomes}
                  />
                </Fragment>
              ))}
            </ul>
          </>
        )}
        <div ref={fim} />
      </div>

      {canal.pode_escrever ? (
        <Composer key={canal.id} canal={canal} resposta={resposta} aoCancelarResposta={() => setResposta(null)} />
      ) : (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-borda p-3">
          <p className="min-w-0 flex-1 basis-56 text-sm text-tinta-2">{canal.arquivado ? t('painel.arquivadoAviso') : !canal.participa ? t('painel.publicoAviso') : t('painel.semEnvio')}</p>
          {!canal.participa && !canal.arquivado && s.pode('equipe.enviar') && (
            <Botao variante="primario" carregando={entrar.isPending} onClick={() => entrar.mutate()}>
              {t('acoes.entrar')}
            </Botao>
          )}
        </div>
      )}

      <Membros canal={canal} aberto={membros} aoFechar={() => setMembros(false)} />
      <EditarCanal canal={canal} aberto={editarCanal} aoFechar={() => setEditarCanal(false)} />
      <Confirmar aberto={!!apagando} aoFechar={() => setApagando(null)} aoConfirmar={() => apagando && apagar.mutate(apagando)} titulo={t('mensagem.apagarTitulo')} texto={t('mensagem.apagarTexto')} perigoso carregando={apagar.isPending} />
      <Confirmar aberto={confirmar === 'sair'} aoFechar={() => setConfirmar(null)} aoConfirmar={() => sair.mutate()} titulo={t('membros.sairTitulo')} texto={t('membros.sairTexto')} perigoso carregando={sair.isPending} />
      <Confirmar aberto={confirmar === 'arquivar'} aoFechar={() => setConfirmar(null)} aoConfirmar={() => arquivar.mutate(true)} titulo={t('membros.arquivarTitulo')} texto={t('membros.arquivarTexto')} carregando={arquivar.isPending} />
    </div>
  );
}
